
import { useState, useRef, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

export const useAudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    audioElementRef.current = new Audio();
    audioElementRef.current.addEventListener('ended', () => {
      setIsPlaying(false);
    });

    return () => {
      if (audioElementRef.current) {
        audioElementRef.current.removeEventListener('ended', () => {
          setIsPlaying(false);
        });
        audioElementRef.current.pause();
      }
    };
  }, []);
  
  const play = async (audioBlob: Blob) => {
    if (!audioElementRef.current) return;
    
    const audioURL = URL.createObjectURL(audioBlob);
    audioElementRef.current.src = audioURL;
    
    try {
      await audioElementRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Error playing audio:', error);
      toast({
        title: "Playback Error",
        description: "Could not play the audio response.",
        variant: "destructive",
      });
    }
  };
  
  const stop = () => {
    if (audioElementRef.current && isPlaying) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };
  
  const reset = () => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      audioElementRef.current.src = '';
      setIsPlaying(false);
    }
  };
  
  return {
    isPlaying,
    play,
    stop,
    reset
  };
};
